import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ShieldCheck, 
  Settings, 
  BarChart3, 
  Image as ImageIcon, 
  Calendar,
  ArrowRight,
  Lock,
  ChevronRight
} from 'lucide-react';

const AdminAccess: React.FC = () => {
  const navigate = useNavigate();

  const handleAdminLogin = () => {
    navigate('/admin/login');
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
        staggerChildren: 0.1
      }
    }
  } as const;

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  } as const;

  const adminFeatures = [
    { title: 'Tours', icon: <ImageIcon className="w-5 h-5" />, desc: 'Itineraries & Gallery' },
    { title: 'Analytics', icon: <BarChart3 className="w-5 h-5" />, desc: 'Performance Data' },
    { title: 'Bookings', icon: <Calendar className="w-5 h-5" />, desc: 'Reservations' },
    { title: 'Settings', icon: <Settings className="w-5 h-5" />, desc: 'Configurations' }
  ];

  return (
    <div className="min-h-screen w-full bg-[#F8FBFF] flex flex-col items-center justify-center p-6 font-sans selection:bg-primary-100 italic-shadows">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none overflow-hidden">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10 w-full max-w-lg"
      >
        {/* Branding & Navigation Context */}
        <motion.div variants={itemVariants} className="flex items-center justify-between mb-8 px-2">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center text-sm font-medium text-primary-600 hover:text-primary-800 transition-colors group"
          >
            <ArrowRight className="w-4 h-4 mr-1.5 rotate-180 group-hover:-translate-x-1 transition-transform" />
            Return to Site
          </button>
          <div className="flex items-center space-x-1.5 text-[10px] font-bold text-primary-400 uppercase tracking-widest">
            <Lock className="w-3 h-3" />
            <span>Secure Access</span>
          </div>
        </motion.div>

        {/* Main Access Card */}
        <motion.div 
          variants={itemVariants}
          className="bg-white rounded-[32px] shadow-[0_32px_64px_-16px_rgba(42,126,206,0.12)] border border-blue-50 overflow-hidden"
        >
          {/* Accent Header */}
          <div className="h-2 bg-gradient-to-r from-primary-400 via-primary-600 to-primary-400" />
          
          <div className="p-10 sm:p-12 text-center">
            <motion.div 
              variants={itemVariants}
              className="inline-flex items-center justify-center w-20 h-20 bg-primary-50 rounded-3xl mb-8 group"
            >
              <ShieldCheck className="w-10 h-10 text-primary-600 group-hover:scale-110 transition-transform duration-500" />
            </motion.div>

            <motion.div variants={itemVariants} className="space-y-3 mb-10">
              <h1 className="text-3xl sm:text-4xl font-extrabold text-primary-900 tracking-tight font-serif">
                Administrator Access
              </h1>
              <p className="text-neutral-500 text-base leading-relaxed max-w-[280px] mx-auto font-medium">
                Verify credentials to manage the SightTrail East African safari infrastructure.
              </p>
            </motion.div>

            {/* Quick Feature Grid - Minimal */}
            <motion.div 
              variants={itemVariants}
              className="grid grid-cols-2 gap-3 mb-10 text-left"
            >
              {adminFeatures.map((f, i) => (
                <div key={i} className="flex items-center p-3 rounded-2xl bg-neutral-50/50 border border-neutral-100 group hover:bg-white hover:shadow-sm transition-all">
                  <div className="text-primary-500 mr-3 opacity-60 group-hover:opacity-100 transition-opacity">
                    {f.icon}
                  </div>
                  <div>
                    <div className="text-xs font-bold text-primary-900">{f.title}</div>
                    <div className="text-[10px] text-neutral-400">{f.desc}</div>
                  </div>
                </div>
              ))}
            </motion.div>

            <motion.button
              variants={itemVariants}
              onClick={handleAdminLogin}
              className="w-full py-4.5 bg-primary-600 hover:bg-primary-700 text-white rounded-2xl font-bold text-lg shadow-xl shadow-primary-900/10 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center group"
            >
              Authorized Login
              <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </div>
        </motion.div>

        {/* Footer info - Toned Down */}
        <motion.div 
          variants={itemVariants}
          className="mt-10 text-center"
        >
          <div className="text-[10px] font-bold text-primary-200 uppercase tracking-[0.3em] mb-4">
            Official SightTrails Administration
          </div>
          <div className="flex items-center justify-center space-x-6 text-[10px] text-neutral-400 font-medium">
            <span className="hover:text-primary-600 cursor-default transition-colors">Privacy Protocal</span>
            <span className="w-1 h-1 bg-neutral-200 rounded-full" />
            <span className="hover:text-primary-600 cursor-default transition-colors">System Support</span>
            <span className="w-1 h-1 bg-neutral-200 rounded-full" />
            <span className="hover:text-primary-600 cursor-default transition-colors">East African Heritage</span>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default AdminAccess;
